import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { isCancel } from "axios";
import { api, apiErr } from "@/lib/api";
import { useInfraSupervision } from "@/hooks/useInfraSupervision";
import { StatCard, Dot } from "@/components/common";
import { fmtTime } from "@/lib/format";
import { toast } from "sonner";
import {
  Server, RefreshCw, Cpu, MemoryStick, HardDrive, Network, Boxes, ShieldAlert,
  ExternalLink, Search, AlertTriangle, RotateCw, Activity, Terminal,
} from "lucide-react";
import { FilesTab, EnvTab, ComposeTab, ShellTab, RaspberryTab, DockerManageTab } from "@/components/infra/InfraAdvanced";
import { InfraStatusBanner, RaspberrySupervision, SystemDetails, EventsPanel, RawPanel, LogsBlock, SelfTestPanel } from "@/components/infra/InfraPanels";
import GatewayOtaTab from "@/components/infra/GatewayOtaTab";
import InfraApiOtaTab from "@/components/infra/InfraApiOtaTab";

const TABS = [
  { id: "overview", label: "Vue globale" },
  { id: "services", label: "Services" },
  { id: "docker", label: "Docker" },
  { id: "wireguard", label: "WireGuard" },
  { id: "files", label: "Fichiers" },
  { id: "env", label: "Variables" },
  { id: "compose", label: "Compose" },
  { id: "shell", label: "Shell" },
  { id: "raspberry", label: "Raspberry" },
  { id: "gateway-ota", label: "Gateway OTA" },
  { id: "infra-ota", label: "Infra API OTA" },
  { id: "logs", label: "Logs" },
  { id: "cockpit", label: "Cockpit" },
  { id: "actions", label: "Actions admin" },
];

const RESTARTABLE = [
  { id: "nginx", label: "Nginx", impact: "Recharge le reverse-proxy. Brève coupure HTTP possible." },
  { id: "wireguard", label: "WireGuard", impact: "Interrompt temporairement les connexions VPN des drones." },
  { id: "cockpit", label: "Cockpit", impact: "Redémarre l'interface de maintenance Cockpit." },
  { id: "infra-api", label: "Infra API", impact: "Redémarre l'API d'infrastructure (supervision indisponible quelques secondes)." },
];

const LOG_SERVICES = ["nginx", "docker", "wireguard", "cockpit", "infra-api"];
const COCKPIT_URL = "https://infra.skycore-ai.com";

const pick = (o, keys) => { for (const k of keys) { const v = k.split(".").reduce((a, c) => (a == null ? a : a[c]), o); if (v != null) return v; } return undefined; };

function pct(v) { const n = Number(v); return Number.isFinite(n) ? `${Math.round(n)}` : "—"; }
function svcColor(active) { return active === true ? "#22C55E" : active === false ? "#EF4444" : "#525252"; }

function normalizeServices(data) {
  let arr = [];
  if (Array.isArray(data)) arr = data;
  else if (Array.isArray(data?.services)) arr = data.services;
  else if (data && typeof data === "object") arr = Object.entries(data).map(([k, v]) => (v && typeof v === "object" ? { name: k, ...v } : { name: k, status: v }));
  return arr.map((s) => {
    const status = typeof s.status === "string" ? s.status : s.active === true ? "active" : s.active === false ? "inactive" : "";
    const active = s.active ?? (typeof s.status === "string" ? /active|running|ok|up/i.test(s.status) : undefined);
    return {
      name: s.name || s.service || s.unit || "—",
      active,
      status: status || (active ? "active" : active === false ? "inactive" : "unknown"),
      description: s.description || s.desc || "",
      last_check: s.last_check || s.checked_at || s.timestamp || "",
    };
  });
}

function findActive(services, patterns) {
  const s = services.find((x) => patterns.some((p) => x.name?.toLowerCase().includes(p)));
  return s?.active;
}

export default function Infrastructure() {
  const [tab, setTab] = useState("overview");
  const [health, setHealth] = useState(null);
  const [system, setSystem] = useState(null);
  const [services, setServices] = useState(null);
  const [wireguard, setWireguard] = useState(null);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [autoRefresh, setAutoRefresh] = useState(15);
  const [resilience, setResilience] = useState(null);
  const [confirm, setConfirm] = useState(null);
  const [busy, setBusy] = useState("");
  const [sudoIssue, setSudoIssue] = useState(null);
  // logs
  const [logService, setLogService] = useState("nginx");
  const [logData, setLogData] = useState(null);
  const [logSearch, setLogSearch] = useState("");
  const [logLoading, setLogLoading] = useState(false);
  const logBoxRef = useRef(null);

  // Une vérification = 1 lot de requêtes parallèles ; jamais deux lots simultanés (useInfraSupervision).
  const tick = useCallback(async (signal) => {
    const get = (url) => api.get(url, { signal }).then((r) => r.data).catch((e) => {
      if (isCancel(e)) return null;
      if (!e.response || e.response.status >= 502) return { ok: false, backendUnreachable: true, error: apiErr(e, "Backend SkyCore injoignable") };
      return { ok: false, status: e.response.status, failure_class: "UPSTREAM_ERROR", error: apiErr(e) };
    });
    const [h, s, sv, rz] = await Promise.all([
      get("/infrastructure/health"), get("/infrastructure/system"), get("/infrastructure/services"), get("/infrastructure/resilience"),
    ]);
    if (signal.aborted) return null;
    setHealth(h);
    // Dernières données valides conservées pendant un échec ponctuel (fraîcheur affichée par la bannière).
    setSystem((prev) => (s?.ok || !prev?.ok ? s : prev));
    setServices((prev) => (sv?.ok || !prev?.ok ? sv : prev));
    setResilience(rz);
    setLoading(false);
    return h;
  }, []);
  const { state: supervision, refresh: refreshSupervision } = useInfraSupervision(tick, autoRefresh * 1000);
  const loadCore = refreshSupervision;

  const loadEvents = useCallback(() => {
    api.get("/infrastructure/events", { params: { limit: 30 } }).then((r) => setEvents(r.data.events || [])).catch(() => {});
  }, []);

  const askConfirm = useCallback((cfg) => setConfirm(cfg), []);
  const reloadAll = useCallback(() => { loadCore(); loadEvents(); }, [loadCore, loadEvents]);
  const [confirmBusy, setConfirmBusy] = useState(false);

  useEffect(() => { loadEvents(); }, [loadEvents]);

  useEffect(() => {
    if (tab === "wireguard" && wireguard == null) api.get("/infrastructure/wireguard").then((r) => setWireguard(r.data)).catch((e) => setWireguard({ ok: false, error: apiErr(e) }));
  }, [tab]); // eslint-disable-line

  const svcList = useMemo(() => normalizeServices(services?.ok ? services.data : null), [services]);
  const infraOk = supervision.status === "ONLINE";
  const coreError = ["OFFLINE", "AUTH_ERROR", "NOT_CONFIGURED"].includes(supervision.status);
  const infraKpi = infraOk ? ["OK", "#22C55E"] : supervision.status === "DEGRADED" || supervision.status === "CHECKING" ? ["?", "#F59E0B"] : ["DOWN", "#EF4444"];

  const cpu = pct(pick(system?.data || {}, ["cpu.percent", "cpu_percent", "cpu_usage", "cpu"]));
  const ram = pct(pick(system?.data || {}, ["memory.percent", "mem.percent", "ram.percent", "ram_percent", "memory_percent"]));
  const disk = pct(pick(system?.data || {}, ["disk.percent", "disk_percent", "disk_usage", "disk"]));

  const kpis = [
    { label: "Infra API", value: infraKpi[0], color: infraKpi[1], icon: Activity },
    { label: "CPU", value: cpu, unit: "%", color: "#3B82F6", icon: Cpu },
    { label: "RAM", value: ram, unit: "%", color: "#A855F7", icon: MemoryStick },
    { label: "Disque", value: disk, unit: "%", color: "#F59E0B", icon: HardDrive },
    { label: "Nginx", value: lblActive(findActive(svcList, ["nginx"])), color: svcColor(findActive(svcList, ["nginx"])), icon: Server },
    { label: "Docker", value: lblActive(findActive(svcList, ["docker"])), color: svcColor(findActive(svcList, ["docker"])), icon: Boxes },
    { label: "WireGuard", value: lblActive(findActive(svcList, ["wg", "wireguard"])), color: svcColor(findActive(svcList, ["wg", "wireguard"])), icon: Network },
    { label: "Cockpit", value: lblActive(findActive(svcList, ["cockpit"])), color: svcColor(findActive(svcList, ["cockpit"])), icon: Terminal },
  ];

  const doRestart = async (service) => {
    setBusy(service);
    try {
      const { data } = await api.post(`/infrastructure/restart/${service}`);
      if (data.ok) { toast.success(`Redémarrage de ${service} demandé`); setSudoIssue(null); }
      else if (data.error_type === "sudo_permission_required") {
        setSudoIssue(service);
        toast.error("Permission sudo manquante côté Infra API", {
          description: "Ajoutez la règle sudoers /etc/sudoers.d/skycore-infra-api (voir SUDOERS_SETUP.md).",
          duration: 8000,
        });
      } else toast.error(data.error || "Action échouée");
    } catch (e) {
      toast.error(apiErr(e, "Action non autorisée"));
    } finally {
      setBusy(""); setConfirm(null);
      loadEvents(); loadCore();
    }
  };

  const loadLogs = async (svc) => {
    setLogLoading(true);
    try {
      const { data } = await api.get(`/infrastructure/logs/${svc}`);
      setLogData(data);
    } catch (e) {
      setLogData({ ok: false, error: apiErr(e) });
    } finally {
      setLogLoading(false);
      loadEvents();
      setTimeout(() => { if (logBoxRef.current) logBoxRef.current.scrollTop = logBoxRef.current.scrollHeight; }, 100);
    }
  };

  return (
    <div className="p-5 space-y-5" data-testid="infrastructure-page">
      {/* Header */}
      <div className="flex items-start gap-3 flex-wrap">
        <div className="w-9 h-9 border border-white/15 rounded-sm flex items-center justify-center"><Server size={18} className="text-[#3B82F6]" /></div>
        <div>
          <h1 className="font-chivo text-2xl font-black tracking-tighter uppercase leading-none">Infrastructure serveur</h1>
          <p className="font-mono text-[11px] text-[#A3A3A3] mt-1">Supervision et administration du serveur central SkyCore.</p>
        </div>
        <div className="ml-auto flex items-center gap-2">
          <select data-testid="auto-refresh" value={autoRefresh} onChange={(e) => setAutoRefresh(Number(e.target.value))}
            className="bg-[#141414] border border-white/15 rounded-sm font-mono text-[10px] px-2 py-2 text-white">
            <option value={0}>Supervision auto : désactivée</option>
            <option value={10}>Supervision auto : 10s</option>
            <option value={15}>Supervision auto : 15s</option>
            <option value={30}>Supervision auto : 30s</option>
          </select>
          <button data-testid="infra-refresh" onClick={() => { loadCore(); loadEvents(); }}
            className="flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-wider border border-white/15 rounded-sm px-3 py-2 hover:bg-white/10"><RefreshCw size={13} /> Rafraîchir</button>
        </div>
      </div>

      <InfraStatusBanner supervision={supervision} />

      {coreError && (
        <div data-testid="infra-error" className="flex items-center gap-2 bg-[#EF4444]/10 border border-[#EF4444]/40 rounded-sm px-4 py-3">
          <AlertTriangle size={15} className="text-[#EF4444]" />
          <span className="font-mono text-[11px] text-[#EF4444]">{supervision.lastError || health?.error || "Infra API indisponible"}</span>
        </div>
      )}

      <RaspberrySupervision res={resilience} infraStatus={supervision.status} />

      {/* Tabs */}
      <div className="flex items-center gap-1 border-b border-white/10 overflow-x-auto">
        {TABS.map((t) => (
          <button key={t.id} data-testid={`tab-${t.id}`} onClick={() => setTab(t.id)}
            className={`px-3 py-2 font-mono text-[11px] uppercase tracking-wider border-b-2 transition-colors whitespace-nowrap ${tab === t.id ? "border-[#3B82F6] text-white" : "border-transparent text-[#A3A3A3] hover:text-white"}`}>{t.label}</button>
        ))}
      </div>

      {loading && <div className="font-mono text-xs text-[#525252]">Chargement...</div>}

      {/* OVERVIEW */}
      {!loading && tab === "overview" && (
        <div className="space-y-5">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="infra-kpis">
            {kpis.map((k) => <StatCard key={k.label} {...k} testid={`kpi-${k.label.toLowerCase()}`} />)}
          </div>
          <SystemDetails system={system} />
          <EventsPanel events={events} />
        </div>
      )}

      {/* SERVICES */}
      {!loading && tab === "services" && (
        <div className="bg-[#141414] border border-white/10 rounded-sm overflow-hidden">
          <table className="w-full text-left">
            <thead>
              <tr className="font-mono text-[9px] uppercase tracking-wider text-[#525252] border-b border-white/10">
                <th className="px-3 py-2">Service</th><th className="px-3 py-2">Statut</th><th className="px-3 py-2">Description</th><th className="px-3 py-2">Dernier check</th><th className="px-3 py-2 text-right">Action</th>
              </tr>
            </thead>
            <tbody data-testid="services-table">
              {svcList.length === 0 && <tr><td colSpan={5} className="px-3 py-4 font-mono text-[11px] text-[#525252]">Aucun service disponible</td></tr>}
              {svcList.map((s) => {
                const restartId = RESTARTABLE.find((r) => s.name?.toLowerCase().includes(r.id === "infra-api" ? "infra" : r.id) || (r.id === "wireguard" && /wg/i.test(s.name)))?.id;
                return (
                  <tr key={s.name} className="border-b border-white/5 font-mono text-[11px]">
                    <td className="px-3 py-2 text-white">{s.name}</td>
                    <td className="px-3 py-2"><span className="flex items-center gap-1.5"><Dot color={svcColor(s.active)} /> <span style={{ color: svcColor(s.active) }}>{s.status}</span></span></td>
                    <td className="px-3 py-2 text-[#A3A3A3]">{s.description || "—"}</td>
                    <td className="px-3 py-2 text-[#525252]">{s.last_check ? fmtTime(s.last_check) : "—"}</td>
                    <td className="px-3 py-2 text-right">
                      {restartId ? (
                        <button data-testid={`svc-restart-${restartId}`} onClick={() => { const r = RESTARTABLE.find((x) => x.id === restartId); askConfirm({ title: `Redémarrer ${r.label}`, critical: true, message: `${r.impact} Confirmer le redémarrage de ${r.label} ?`, onYes: () => doRestart(r.id) }); }}
                          className="inline-flex items-center gap-1 font-mono text-[9px] uppercase tracking-wider border border-[#F59E0B]/40 text-[#F59E0B] rounded-sm px-2 py-1 hover:bg-[#F59E0B]/10"><RotateCw size={11} /> Redémarrer</button>
                      ) : <span className="text-[#525252]">—</span>}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* DOCKER */}
      {!loading && tab === "docker" && <DockerManageTab askConfirm={askConfirm} reload={reloadAll} />}

      {/* WIREGUARD */}
      {!loading && tab === "wireguard" && <RawPanel title="WireGuard (wg show)" res={wireguard} testid="wireguard-panel" icon={Network} note="Vérifiez rapidement si le VPN drone est actif et les handshakes récents. Les clés sont masquées." />}

      {/* FILES */}
      {!loading && tab === "files" && <FilesTab askConfirm={askConfirm} reload={reloadAll} />}

      {/* ENV */}
      {!loading && tab === "env" && <EnvTab askConfirm={askConfirm} reload={reloadAll} />}

      {/* COMPOSE */}
      {!loading && tab === "compose" && <ComposeTab askConfirm={askConfirm} reload={reloadAll} />}

      {/* SHELL */}
      {!loading && tab === "shell" && <ShellTab askConfirm={askConfirm} reload={reloadAll} />}

      {/* RASPBERRY */}
      {!loading && tab === "raspberry" && <RaspberryTab askConfirm={askConfirm} reload={reloadAll} />}

      {/* GATEWAY OTA — exclusivement ici (jamais dans /drone-registry). Données réelles + gating
          d'activation piloté par l'autorité backend GET /gateway-ota/preflight (INFRA-OTA 1C.1). */}
      {!loading && tab === "gateway-ota" && <GatewayOtaTab />}

      {!loading && tab === "infra-ota" && <InfraApiOtaTab />}

      {/* LOGS */}
      {!loading && tab === "logs" && (
        <div className="space-y-3">
          <div className="flex items-center gap-2 flex-wrap">
            <select data-testid="log-service" value={logService} onChange={(e) => setLogService(e.target.value)}
              className="bg-[#141414] border border-white/15 rounded-sm font-mono text-[11px] px-2 py-2 text-white">
              {LOG_SERVICES.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
            <button data-testid="log-load" onClick={() => loadLogs(logService)} className="font-mono text-[10px] uppercase tracking-wider bg-[#3B82F6] rounded-sm px-3 py-2 hover:bg-[#3B82F6]/85">Charger</button>
            <button data-testid="log-refresh" onClick={() => loadLogs(logService)} className="flex items-center gap-1 font-mono text-[10px] uppercase tracking-wider border border-white/15 rounded-sm px-3 py-2 hover:bg-white/10"><RefreshCw size={12} /> Rafraîchir</button>
            <div className="flex items-center gap-1.5 bg-[#141414] border border-white/15 rounded-sm px-2 py-1.5 ml-auto">
              <Search size={12} className="text-[#525252]" />
              <input data-testid="log-search" value={logSearch} onChange={(e) => setLogSearch(e.target.value)} placeholder="Recherche..."
                className="bg-transparent font-mono text-[11px] text-white outline-none w-40" />
            </div>
          </div>
          <LogsBlock res={logData} loading={logLoading} search={logSearch} boxRef={logBoxRef} />
        </div>
      )}

      {/* COCKPIT */}
      {!loading && tab === "cockpit" && (
        <div className="bg-[#141414] border border-white/10 rounded-sm p-5 max-w-xl space-y-3" data-testid="cockpit-panel">
          <div className="flex items-center gap-2"><Terminal size={16} className="text-[#3B82F6]" /><span className="font-chivo font-black uppercase tracking-tight">Cockpit serveur</span></div>
          <div className="font-mono text-[11px] text-[#A3A3A3]">URL : <span className="text-white">{COCKPIT_URL}</span></div>
          <a data-testid="open-cockpit" href={COCKPIT_URL} target="_blank" rel="noreferrer"
            className="inline-flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-wider bg-[#3B82F6] rounded-sm px-3 py-2 hover:bg-[#3B82F6]/85"><ExternalLink size={13} /> Ouvrir Cockpit</a>
          <p className="font-mono text-[10px] text-[#F59E0B] bg-[#F59E0B]/10 border border-[#F59E0B]/30 rounded-sm px-3 py-2">
            Cockpit est réservé à la maintenance avancée. Les actions courantes doivent être faites depuis SkyCore AI.
          </p>
        </div>
      )}

      {/* ACTIONS */}
      {!loading && tab === "actions" && (
        <div className="space-y-3 max-w-2xl">
          <div className="flex items-center gap-2 font-mono text-[11px] uppercase tracking-wider text-[#F59E0B]"><ShieldAlert size={14} /> Actions sensibles — admin uniquement</div>
          {sudoIssue && (
            <div data-testid="sudo-permission-banner" className="flex items-start gap-2 border border-[#EF4444]/40 bg-[#EF4444]/10 rounded-sm px-4 py-3">
              <ShieldAlert size={16} className="text-[#EF4444] shrink-0 mt-0.5" />
              <div className="font-mono text-[11px] text-[#EF4444]">
                <span className="font-bold uppercase mr-2 border border-[#EF4444]/50 rounded px-1">Sudo requis</span>
                Permission sudo manquante côté Infra API.
                <div className="text-[#F59E0B] mt-1">Action requise : ajouter la règle sudoers <code>/etc/sudoers.d/skycore-infra-api</code> (voir <code>SUDOERS_SETUP.md</code>).</div>
              </div>
            </div>
          )}
          <SelfTestPanel />
          <div className="grid gap-2">
            {RESTARTABLE.map((r) => (
              <div key={r.id} className="flex items-center gap-3 bg-[#141414] border border-white/10 rounded-sm px-4 py-3">
                <RotateCw size={15} className="text-[#F59E0B]" />
                <div className="min-w-0">
                  <div className="font-mono text-xs text-white">Redémarrer {r.label}</div>
                  <div className="font-mono text-[10px] text-[#A3A3A3]">{r.impact}</div>
                </div>
                <button data-testid={`restart-${r.id}`} onClick={() => askConfirm({ title: `Redémarrer ${r.label}`, critical: true, message: `${r.impact} Confirmer le redémarrage de ${r.label} ?`, onYes: () => doRestart(r.id) })} disabled={busy === r.id}
                  className="ml-auto font-mono text-[10px] uppercase tracking-wider border border-[#F59E0B]/40 text-[#F59E0B] rounded-sm px-3 py-2 hover:bg-[#F59E0B]/10 disabled:opacity-40">Redémarrer</button>
              </div>
            ))}
          </div>
          <EventsPanel events={events} />
        </div>
      )}

      {/* Confirm dialog (generic) */}
      {confirm && (
        <div className="fixed inset-0 z-[2000] flex items-center justify-center bg-black/70 p-4" data-testid="infra-confirm">
          <div className="w-full max-w-md bg-[#141414] border border-white/15 rounded-sm p-5 space-y-4">
            <div className={`flex items-center gap-2 ${confirm.critical ? "text-[#EF4444]" : "text-[#F59E0B]"}`}>
              <AlertTriangle size={18} /><span className="font-chivo font-black uppercase tracking-tight">{confirm.title || "Confirmer l'action"}</span>
            </div>
            <p className="font-mono text-[12px] text-[#E5E5E5] whitespace-pre-wrap">{confirm.message}</p>
            <div className="flex justify-end gap-2">
              <button data-testid="infra-confirm-cancel" onClick={() => setConfirm(null)} disabled={confirmBusy} className="font-mono text-[10px] uppercase tracking-wider border border-white/15 rounded-sm px-3 py-2 hover:bg-white/10 disabled:opacity-40">Annuler</button>
              <button data-testid="infra-confirm-yes"
                onClick={async () => {
                  const fn = confirm.onYes;
                  setConfirm(null);
                  if (fn) { setConfirmBusy(true); try { await fn(); } finally { setConfirmBusy(false); } }
                }}
                disabled={confirmBusy}
                className={`font-mono text-[10px] uppercase tracking-wider rounded-sm px-4 py-2 disabled:opacity-50 ${confirm.critical ? "bg-[#EF4444] hover:bg-[#EF4444]/85" : "bg-[#3B82F6] hover:bg-[#3B82F6]/85"}`}>
                {confirmBusy ? "..." : "Confirmer"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function lblActive(a) { return a === true ? "OK" : a === false ? "DOWN" : "—"; }
